// Runtime Firebase init from backend route (Secret Manager-backed)

import { initializeApp, type FirebaseApp } from "firebase/app";
import { getAuth, type Auth } from "firebase/auth";
import { 
  initializeFirestore, 
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
  setLogLevel
} from "firebase/firestore";
import { getStorage, type FirebaseStorage } from "firebase/storage";

let app: FirebaseApp | null = null;
let authInst: Auth | null = null;
let dbInst: Firestore | null = null;
let storageInst: FirebaseStorage | null = null;

const CACHE_KEY = 'firebase_config_cache';
const CACHE_EXPIRY_KEY = 'firebase_config_expiry';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

async function fetchConfig() {
  // Check localStorage cache first
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    const expiry = localStorage.getItem(CACHE_EXPIRY_KEY);
    
    if (cached && expiry) {
      const expiryTime = parseInt(expiry, 10);
      if (Date.now() < expiryTime) {
        console.log('[Firebase] Using cached config');
        return JSON.parse(cached);
      }
    }
  } catch (err) {
    console.warn('[Firebase] Cache read failed:', err);
  }

  // Fetch from server if cache miss or expired
  console.log('[Firebase] Fetching config from server');
  const res = await fetch("/api/config/firebase", { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load Firebase config");
  const config = await res.json();
  
  // Cache the config
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(config));
    localStorage.setItem(CACHE_EXPIRY_KEY, (Date.now() + CACHE_DURATION).toString());
  } catch (err) {
    console.warn('[Firebase] Cache write failed:', err);
  }
  
  return config;
}

// Call this once at app start (before React renders)
export async function initFirebase() {
  if (app) return;               // already initialized
  
  // Disable verbose Firestore logging to prevent console spam
  // Only log errors and warnings
  if (process.env.NODE_ENV === 'development') {
    setLogLevel('error'); // Changed from 'debug' to 'error'
    console.log('[Firebase] Initialized with error-level logging');
  }
  
  const config = await fetchConfig();
  app = initializeApp(config);
  authInst = getAuth(app);
  
  // Initialize Firestore with persistent local cache for better performance
  // This enables offline caching and reduces network calls
  dbInst = initializeFirestore(app, {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager()
    })
  });
  
  storageInst = getStorage(app);
  
  // Suppress harmless network reconnection errors in console (happens after computer sleep)
  if (typeof window !== 'undefined') {
    const originalError = console.error;
    console.error = (...args) => {
      const msg = args[0]?.toString() || '';
      // Filter out Firestore reconnection errors
      if (msg.includes('QUIC_PROTOCOL_ERROR') || 
          msg.includes('message channel closed') ||
          msg.includes('Firestore/Listen/channel')) {
        return;
      }
      originalError.apply(console, args);
    };
  }
}

// Synchronous getters (safe AFTER initFirebase has run)
export function auth(): Auth {
  if (!authInst) throw new Error("Firebase not initialized. Call initFirebase() first.");
  return authInst;
}
export function db(): Firestore {
  if (!dbInst) throw new Error("Firebase not initialized. Call initFirebase() first.");
  return dbInst;
}
export function storage(): FirebaseStorage {
  if (!storageInst) throw new Error("Firebase not initialized. Call initFirebase() first.");
  return storageInst;
}

